import type {
  ButtonNavItem,
  LanguageNavItem,
  PageNavItem,
  ThemeNavItem,
  UrlNavItem,
} from '@unifyapps/defs/types/interface';
import { useTranslation } from '@unifyapps/i18n/client';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import { useCallback } from 'react';
import { useExecuteLogout } from '../hooks/useExecuteLogout';
import { useRouter } from '../navigation';
import { hasUnresolvedDynamicPathParams } from '../no-code/utils/path/interfacePathDynamicParamsHandlers';

export function useUserMenuAction({
  userMenuNavItems,
  onClose,
}: {
  userMenuNavItems?: (UrlNavItem | ThemeNavItem | LanguageNavItem | ButtonNavItem | PageNavItem)[];
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const { onLogout } = useExecuteLogout();
  const { showSnackbar } = useSnackbar();

  const onAction = useCallback(
    async (menuItemId: string) => {
      if (menuItemId === 'logout') {
        try {
          await onLogout();
          router.replace('/');
        } catch {
          showSnackbar({
            color: 'error',
            title: t('common:ErrorState.SomethingWentWrong'),
          });
        }
        return;
      }

      const clickedMenuItem = userMenuNavItems?.find(
        (item) => item.id === menuItemId,
      ) as UrlNavItem;

      if (clickedMenuItem && 'url' in clickedMenuItem) {
        const isInvalidUrl = hasUnresolvedDynamicPathParams(clickedMenuItem.url ?? '');

        if (isInvalidUrl) {
          console.error('Dynamic Params not passed properly', clickedMenuItem);
          return;
        }

        if (clickedMenuItem.openInNewTab) {
          window.open(clickedMenuItem.url, '_blank', 'noreferrer noopener');
        } else {
          router.push(clickedMenuItem.url);
        }
        onClose?.();
      }
    },
    [router, showSnackbar, t, onLogout, userMenuNavItems, onClose],
  );

  return { onAction };
}
